import React, { Component } from 'react'

import { RouteComponentProps } from 'react-router-dom'
import { WithTranslation } from 'react-i18next'
import { ChildProps, Query } from 'react-apollo'
import { InputProps } from './positionQuery'
import { CALCULATION_PROJECT_QUERY } from './calculationProjectQuery'
import { PROJECT_QUERY } from './projectQuery'
import { POSITION_IMAGE_QUERY } from './positionImageQuery'
import { AuthStore } from 'redux/reducers/auth'

import {
  formatUnit,
  formatNumber,
  getPrintTemplateLink,
  getProjectId,
  getManufacturer,
} from 'utils'
import isNil from 'lodash/isNil'
import isNumber from 'lodash/isNumber'
import get from 'lodash/get'
import defaultTo from 'lodash/defaultTo'
import isEmpty from 'lodash/isEmpty'
import debounce from 'lodash/debounce'
import find from 'lodash/find'
import set from 'lodash/set'

import Button from 'components/ui/button'
import Head from 'components/layout/head'
import PageLayout from 'components/layout/pageLayout'
import Select, { IOption } from 'components/ui/select'
import SubNavigation from 'components/layout/subNavigation'
import PopUpMenu from 'components/ui/popUpMenu'
import Accordion from 'components/layout/accordion'
import Input from 'components/ui/input'
import EditableTable, { IEditableItem } from 'components/layout/editableTable'
import TableResult from 'components/layout/tableResult'
import Modal from 'components/ui/modal'
import { SaveMode } from 'components/layout/editableTable/editableTableRow'

import wageCostsConfig from './wageCostsTable.config'
import materialCostsConfig from './materialCostsTable.config'
import netResultTableConfig from './netResultTable.config'
import grossResultTableConfig from './grossResultTable.config'
import Loader from 'components/ui/loader'

interface CalculationOverviewProps
  extends ChildProps<InputProps, Response>,
    RouteComponentProps<any>,
    WithTranslation {
  auth: AuthStore
  positionData: any
  changePositionAmount: any
  changePositionClipping: any
  deletePosition: any
  customizeElementMutation: any
  changePositionFilter: any
  changePositionVAT: any
  changePositionHourlyRate: any
}

interface IErrorObject {
  [k: string]: string | null
}

type IFilter = {
  name: string
  shortName: string
  unit: string
  selectedValue: string
  id: string
  values: string[]
  condition: string
}

type CalculationOverviewState = {
  loading: boolean
  errors: IErrorObject | null
  success: boolean
  modalIsOpen: boolean
  amount: number | null
  clipping: number | null
  vat: number | null
  hourlyRate: number | null
  filters: IFilter[]
  annotation: string | null
}

class CalculationOverview extends Component<CalculationOverviewProps, CalculationOverviewState> {
  constructor(props: CalculationOverviewProps) {
    super(props)
    this.state = {
      loading: false,
      errors: null,
      success: false,
      modalIsOpen: false,
      amount: null,
      clipping: null,
      vat: null,
      hourlyRate: null,
      filters: [],
      annotation: null,
    }
  }

  componentWillReceiveProps = (nextProps: CalculationOverviewProps) => {
    const filters = defaultTo(get(nextProps, 'positionData.getPosition.filters'), [])

    if (filters !== this.state.filters) {
      this.setState({ filters })
    }
  }

  startLoading = () => {
    this.setState({ loading: true })
  }

  stopLoading = () => {
    this.setState({ loading: false })
  }

  handleCalculationChange = (option: IOption) => {
    const { history } = this.props
    const projectId = getProjectId(this.props)
    const calculationId = String(option.value)

    history.push(
      projectId
        ? `/projects/${projectId}/calculations/${calculationId}`
        : `/calculations/${calculationId}`,
    )
  }

  handlePrint = () => {
    const {
      auth: { apiToken },
    } = this.props
    const { calculationId } = this.props.match.params
    const projectId = getProjectId(this.props)
    const { projectNumber: calcProjectId } = this.props.calcProject

    const version = !isNil(projectId) ? 'v1' : calculationId
    const eleId = !isNil(projectId) ? Number(calculationId) : 1
    const projNr = !isNil(projectId) ? projectId : calcProjectId

    if (!isNil(projNr)) {
      window.open(
        getPrintTemplateLink('calculation', {
          version,
          projNr,
          eleId,
          token: defaultTo(apiToken, ''),
        }),
        '_blank',
      )
    }
  }

  handleDeletion = () => {
    const { deletePosition: mutate, history } = this.props
    const { calculationId } = this.props.match.params
    const projectId = getProjectId(this.props)
    const { projectNumber: calcProjectId } = this.props.calcProject

    if (mutate) {
      mutate({
        variables: {
          version: !isNil(projectId) ? 'v1' : calculationId,
          positionId: !isNil(projectId) ? Number(calculationId) : 1,
          projectId: !isNil(projectId) ? projectId : calcProjectId,
        },
        refetchQueries: ['getPosition'],
      })
        .then((res: any) => {
          // Delete position was sucessfull
          if (get(res, 'data.deletePosition')) {
            // Redirect back to the overview
            history.push(
              !isNil(projectId) ? `/projects/${projectId}/calculations` : `/calculations`,
            )
          }
        })
        .catch(() => {})
    }
  }

  mutateAmount = debounce((amount: number) => {
    const { changePositionAmount: mutate } = this.props
    const { calculationId } = this.props.match.params
    const projectId = getProjectId(this.props)
    const { projectNumber: calcProjectId } = this.props.calcProject

    if (mutate) {
      this.startLoading()

      mutate({
        variables: {
          amount,
          version: !isNil(projectId) ? 'v1' : calculationId,
          positionId: !isNil(projectId) ? Number(calculationId) : 1,
          projectId: !isNil(projectId) ? projectId : calcProjectId,
        },
        refetchQueries: ['getPosition'],
      })
        .then((res: any) => {
          // Amount change was successful
          if (get(res, 'data.changePositionAmount')) {
            this.showSuccessMessage()
          } else {
            this.showErrorMessage()
          }
        })
        .catch((err: any) => {
          console.error(err)
          this.showErrorMessage()
        })
    }
  }, 300)

  mutateClipping = (clipping: number) => {
    const { changePositionClipping: mutate } = this.props
    const { calculationId } = this.props.match.params
    const projectId = getProjectId(this.props)
    const { projectNumber: calcProjectId } = this.props.calcProject

    if (mutate) {
      this.startLoading()

      mutate({
        variables: {
          clipping,
          version: !isNil(projectId) ? 'v1' : calculationId,
          positionId: !isNil(projectId) ? Number(calculationId) : 1,
          projectId: !isNil(projectId) ? projectId : calcProjectId,
        },
        refetchQueries: ['getPosition'],
      })
        .then((res: any) => {
          // Clipping change was successful
          if (get(res, 'data.addClippingToMaterial')) {
            this.showSuccessMessage()

            this.setState({
              clipping: 0,
            })
          } else {
            this.showErrorMessage()
          }
        })
        .catch((err: any) => {
          console.error(err)
          this.showErrorMessage()
        })
    }
  }

  mutateVAT = debounce((vat: number) => {
    const { changePositionVAT: mutate } = this.props
    const { calculationId } = this.props.match.params

    if (mutate) {
      this.startLoading()

      mutate({
        variables: {
          vat,
          version: calculationId,
        },
        refetchQueries: ['getPosition'],
      })
        .then((res: any) => {
          this.stopLoading()

          // VAT change was successful
          if (get(res, 'data.changeCalcVAT')) {
            this.showSuccessMessage()
          } else {
            this.showErrorMessage()
          }
        })
        .catch((err: any) => {
          console.error(err)
          this.showErrorMessage()
          this.stopLoading()
        })
    }
  }, 300)

  mutateHourlyRate = debounce((hourlyRate: number) => {
    const { changePositionHourlyRate: mutate } = this.props
    const { calculationId } = this.props.match.params

    if (mutate) {
      this.startLoading()

      mutate({
        variables: {
          hourlyRate,
          version: calculationId,
        },
        refetchQueries: ['getPosition'],
      })
        .then((res: any) => {
          this.stopLoading()

          // VAT change was successful
          if (get(res, 'data.changeCalcHourlyRate')) {
            this.showSuccessMessage()
          } else {
            this.showErrorMessage()
          }
        })
        .catch((err: any) => {
          console.error(err)
          this.showErrorMessage()
          this.stopLoading()
        })
    }
  }, 300)

  handleAmountChange = (value: string | number) => {
    const amount = Number(value)

    this.mutateAmount(amount)
    this.setState({
      amount,
    })
  }

  handleClippingChange = (value: string | number) => {
    const clipping = Number(value)

    this.setState({
      clipping,
    })
  }

  handleApplyClipping = () => {
    const { clipping } = this.state

    if (clipping) {
      this.mutateClipping(clipping)
    }
  }

  handleVATChange = (value: string | number) => {
    const vat = Number(value)

    this.mutateVAT(vat)
    this.setState({
      vat,
    })
  }

  handleHourlyRateChange = (value: string | number) => {
    const hourlyRate = Number(value)

    this.mutateHourlyRate(hourlyRate)
    this.setState({
      hourlyRate,
    })
  }

  handleMaterialChange = (saveMode: SaveMode, item: IEditableItem) => {
    const { customizeElementMutation: mutate } = this.props
    const { calculationId } = this.props.match.params
    const projectId = getProjectId(this.props)
    const { projectNumber: calcProjectId } = this.props.calcProject
    const recId = item.id
    const version = !isNil(projectId) ? 'v1' : calculationId
    const articleNumber = get(item, 'data.articleNumber', undefined)
    const amount = get(item, 'data.amount.value', undefined)
    const clippingAmount = get(item, 'data.amountWithClipping.editData.value', undefined)
    const price = defaultTo(
      get(item, 'data.unitPrice.value', undefined),
      get(item, 'data.unitPrice', undefined),
    )

    if (!isNil(mutate)) {
      this.startLoading()

      mutate({
        variables: {
          recId,
          version,
          positionId: !isNil(projectId) ? Number(calculationId) : 1,
          projectId: !isNil(projectId) ? projectId : calcProjectId,
          articleNumber,
          amount,
          clippingAmount,
          price,
          customPrice: saveMode,
        },
        refetchQueries: ['getPosition'],
      })
        .then((res: any) => {
          this.stopLoading()

          // Customize element was successful
          if (get(res, 'data.customizeElement')) {
            this.showSuccessMessage()
          } else {
            this.showErrorMessage()
          }
        })
        .catch((err: any) => {
          console.error(err)
          this.showErrorMessage()
          this.stopLoading()
        })
    }
  }

  showSuccessMessage = () => {
    this.setState({ errors: null, success: true, loading: false })

    // Toggle success message after 3 seconds
    setTimeout(() => {
      this.setState({ success: false })
    }, 3000)
  }

  showErrorMessage = () => {
    this.setState({
      errors: {
        api: 'Ein Fehler ist aufgetreten',
      },
      success: false,
      loading: false,
    })
  }

  getValue = (key: string, onlyProps: boolean = false) => {
    const position = get(this.props, 'positionData.getPosition', undefined)
    const stateValue = get(this.state, key)

    if (!isNil(stateValue) && !onlyProps) {
      return stateValue
    }

    return get(position, key)
  }

  transformCalculationsToOptions = (calculations: any) =>
    calculations.map((item: any) => ({
      value: item.id,
      label: item.systemDescription,
    }))

  transformCosts = (items: any) =>
    items.map((item: any) => {
      const { id, ...data } = item

      Object.keys(data).map((key) => {
        // If amountWithClipping is null use value from amount
        if (key === 'amountWithClipping' && isNil(data[key])) {
          data[key] = {
            value: defaultTo(item.amount, 0),
            unit: item.unit,
            editData: {
              value: defaultTo(item.clippingPercentage, 0),
              unit: '%',
            },
          }
        }

        if (!isNil(data[key]) && !data[key].hasOwnProperty('value')) {
          switch (key) {
            // Add unit to amount data
            case 'amount':
              data[key] = { value: data[key], unit: item.unit }
              break
            // Add unit to amountWithClipping data
            case 'amountWithClipping':
              data[key] = {
                value: data[key],
                unit: item.unit,
                editData: {
                  value: defaultTo(item.clippingPercentage, 0),
                  unit: '%',
                },
              }
              break
          }
        }

        return data[key]
      })

      return {
        id,
        productName: item.product,
        data,
      }
    })

  handleFilterChange = (filterId: string, value: string | number) => {
    const { changePositionFilter: mutate } = this.props
    const { calculationId } = this.props.match.params
    const projectId = getProjectId(this.props)
    const { projectNumber: calcProjectId } = this.props.calcProject
    const version = !isNil(projectId) ? 'v1' : calculationId
    let newFilters = this.state.filters

    // Search for filter with id
    let item = find(newFilters, { id: filterId })

    /*
     * When found set the new value of the filter
     * and set the new state.
     */
    if (item) {
      set(item, 'selectedValue', value)
      this.setState({
        filters: newFilters,
      })
    }

    if (!isNil(mutate)) {
      mutate({
        variables: {
          version,
          positionId: !isNil(projectId) ? Number(calculationId) : 1,
          projectId: !isNil(projectId) ? projectId : calcProjectId,
          filterId,
          valuet: !isNumber(value) ? value : undefined,
          valuen: isNumber(value) ? value : undefined,
        },
        refetchQueries: ['getPosition'],
      })
        .then((res: any) => {
          // Filter change was successful
          if (get(res, 'data.changePositionFilter')) {
            this.showSuccessMessage()
          } else {
            this.showErrorMessage()
          }
        })
        .catch((err: any) => {
          console.error(err)
          this.showErrorMessage()
        })
    }
  }

  renderFilters = () => {
    const { filters } = this.state
    let rows: any[][] = []
    let tmpCol: any[] = []

    filters.forEach((filter: IFilter, index: number) => {
      const { id, name, shortName, unit, selectedValue } = filter

      const isEndOfRow = !((index + 1) % 2)
      const hasOddNumberOfFilters = filters.length % 2
      const isLastFilter = index === filters.length - 1

      const options = (filter.values || []).map((value: string) => ({
        label: value === 'X' ? 'Ja' : value === '-' ? 'Nein' : value,
        value,
      }))

      tmpCol.push(
        <div key={id} className={`w-6/12 flex items-end ${isEndOfRow ? 'ml-30' : ''}`}>
          {isEmpty(options) ? (
            <Input
              type="number"
              name={shortName}
              label={name}
              leftAlign={get(unit, 'length') ? false : true}
              unit={get(unit, 'length') ? unit : undefined}
              value={defaultTo(selectedValue, '')}
              onBlur={(value) => this.handleFilterChange(id, Number(value))}
            />
          ) : (
            <Select
              label={name}
              options={options}
              defaultValue={defaultTo(selectedValue, '')}
              onChange={(option: IOption) => this.handleFilterChange(id, String(option.value))}
            />
          )}
        </div>,
      )

      // Add dummy container to preserve grid
      if (hasOddNumberOfFilters && isLastFilter) {
        tmpCol.push(<div key={index + 1} className="w-6/12 flex items-end ml-30" />)
      }

      // End of row
      if (isEndOfRow || isLastFilter) {
        rows.push(tmpCol)
        tmpCol = []
      }
    })

    return rows.map((row: any, index: number) => (
      <div key={index} className="flex flex-row mb-30 items-end">
        {row.map((col: any) => col)}
      </div>
    ))
  }

  isFastCalculation = isNil(getProjectId(this.props))

  renderContent = () => {
    const { calculationId } = this.props.match.params
    const projectId = getProjectId(this.props)
    const { t } = this.props
    const { modalIsOpen } = this.state

    const isLoading = get(this.props, 'positionData.loading', true) || get(this.state, 'loading')
    const apiErrorCode = get(
      this.props,
      'positionData.error.graphQLErrors[0].message.statusCode',
      undefined,
    )
    const materials = defaultTo(
      get(this.props, 'positionData.getPosition.materialCosts.materials'),
      [],
    )
    const materialsPrice = defaultTo(
      get(this.props, 'positionData.getPosition.materialCosts.totalPrice'),
      0,
    )
    const activities = defaultTo(
      get(this.props, 'positionData.getPosition.timeCosts.activities'),
      [],
    )
    const timeCostsPrice = defaultTo(
      get(this.props, 'positionData.getPosition.timeCosts.totalPrice'),
      0,
    )
    const amount = defaultTo(this.getValue('amount', true), 0)
    const clipping = defaultTo(this.getValue('clipping', true), 0)
    const vat = defaultTo(this.getValue('vat', true), 0)
    const nettoPrice = defaultTo(this.getValue('unitPrice'), 0)
    const totalPriceNetto = defaultTo(this.getValue('totalPrice'), 0)
    const vatPrice = totalPriceNetto * (vat / 100)
    const totalPriceBrutto = defaultTo(this.getValue('totalPriceBrutto'), 0)

    if (apiErrorCode === 409) {
      return (
        <div className="my-65 px-48">
          <p className="font-400 text-15 tracking-0.1 text-black leading-22">
            Kalkulation existiert nicht.
          </p>
        </div>
      )
    } else {
      return (
        <>
          <SubNavigation
            items={[
              {
                label: t('overview'),
                link: projectId
                  ? `/projects/${projectId}/calculations/${calculationId}`
                  : `/calculations/${calculationId}`,
              },
              {
                link: projectId
                  ? `/projects/${projectId}/calculations/${calculationId}/material-requirement`
                  : `/calculations/${calculationId}/material-requirement`,
                label: t('materialRequirement'),
              },
              {
                link: projectId
                  ? `/projects/${projectId}/calculations/${calculationId}/time-requirement`
                  : `/calculations/${calculationId}/time-requirement`,
                label: t('timeRequirement'),
              },
              // {
              //   link: projectId
              //     ? `/projects/${projectId}/calculations/${calculationId}/certificate`
              //     : `/calculations/${calculationId}/certificate`,
              //   label: t('certificate'),
              // },
            ]}
            actions={
              <>
                <PopUpMenu
                  className="ml-30"
                  options={[
                    {
                      label: t('printCalculation'),
                      function: () => this.handlePrint(),
                    },
                    {
                      label: t('deleteCalculation'),
                      function: () => this.handleDeletion(),
                    },
                  ]}
                />
              </>
            }
          />

          {isLoading ? (
            <Loader />
          ) : (
            <>
              <div className="mb-65">
                <Accordion heading={t('configurationBasicSettings')} preExpanded={true}>
                  <div className="px-48">
                    {this.isFastCalculation && (
                      <div className="flex mb-45">
                        <div className="w-2/12 flex items-end">
                          <Input
                            type="number"
                            name="hourlyRate"
                            label="Stundenlohn"
                            unit="EUR"
                            value={defaultTo(this.getValue('hourlyRate', true), 0)}
                            onBlur={this.handleHourlyRateChange}
                          />
                        </div>
                        <div className="w-2/12 ml-30 flex items-end">
                          <Input
                            type="number"
                            name="vat"
                            value={vat}
                            label="MwSt."
                            unit="%"
                            onBlur={this.handleVATChange}
                          />
                        </div>
                      </div>
                    )}

                    <div className="flex mb-45">
                      <div className="flex justify-between w-6/12">
                        <div className="w-8/12">
                          <label className="label">{t('system')}</label>
                          <p className="font-500 text-black tracking-0.12 text-15">
                            {this.getValue('name')}
                          </p>
                        </div>
                        <div
                          style={{ width: '100px' }}
                          className="ml-30 flex items-end bg-gray-200"
                        >
                          <Query
                            query={POSITION_IMAGE_QUERY}
                            variables={{
                              positionId: Number(this.getValue('mainId')),
                              companyId: getManufacturer(),
                            }}
                            fetchPolicy="network-only"
                          >
                            {({ loading, error, data }: any | null) => {
                              if (error || loading) return null

                              const image = get(data, 'getPositionImage', '')

                              if (isNil(image) || image === '') return null

                              return (
                                <>
                                  <Modal
                                    big
                                    headline={`${this.getValue('name')}`}
                                    open={modalIsOpen}
                                    onClose={() =>
                                      this.setState({
                                        modalIsOpen: false,
                                      })
                                    }
                                  >
                                    <img
                                      src={image}
                                      alt={`${this.getValue('name')}`}
                                      aria-hidden={modalIsOpen}
                                    />
                                  </Modal>
                                  <img
                                    onClick={() =>
                                      this.setState({
                                        modalIsOpen: true,
                                      })
                                    }
                                    src={image}
                                    alt={`${this.getValue('name')}`}
                                  />
                                </>
                              )
                            }}
                          </Query>
                        </div>
                      </div>

                      <div className="flex items-end w-6/12 ml-30">
                        <div className="w-4/12">
                          <Input
                            type="number"
                            name="amount"
                            label={t('amount')}
                            unit="m²"
                            value={amount}
                            onBlur={this.handleAmountChange}
                          />
                        </div>
                        <div className="w-4/12 ml-30">
                          <Input
                            type="number"
                            name="clipping"
                            label={t('surchargeWaste')}
                            unit="%"
                            value={clipping}
                            onChange={this.handleClippingChange}
                          />
                        </div>

                        <Button
                          className="ml-10"
                          primary
                          onClick={() => this.handleApplyClipping()}
                        >
                          {t('apply')}
                        </Button>
                      </div>
                    </div>

                    {this.renderFilters()}
                  </div>
                </Accordion>
              </div>

              <div className="mb-65">
                <Accordion heading={t('materialCostsPerM2System')} preExpanded={true}>
                  <EditableTable
                    config={materialCostsConfig(t)}
                    data={this.transformCosts(materials)}
                    onChange={this.handleMaterialChange}
                    totalPrice={materialsPrice}
                  />
                </Accordion>
              </div>

              <div className="mb-65">
                <Accordion heading="Lohnkosten pro m² System" preExpanded={true}>
                  <EditableTable
                    config={wageCostsConfig}
                    data={this.transformCosts(activities)}
                    onChange={this.handleMaterialChange}
                    totalPrice={timeCostsPrice}
                  />
                </Accordion>
              </div>

              {/* <div className="mb-65">
      <Accordion heading="Mehrkosten pro m² System" preExpanded={true}>
        <EditableTable config={additionalCostsConfig} data={[]} />
      </Accordion>
    </div> */}

              <div className="mb-45">
                <Accordion heading="Kalkulationsergebnis" preExpanded={true}>
                  <TableResult
                    config={netResultTableConfig}
                    data={[
                      {
                        amount: {
                          value: amount,
                          unit: get(this.props, 'positionData.getPosition.unit'),
                        },
                        nettoPrice: {
                          value: nettoPrice,
                          unit: `EUR / ${formatUnit(
                            get(this.props, 'positionData.getPosition.unit'),
                          )}`,
                        },
                        totalPriceNetto,
                      },
                    ]}
                  />
                  <TableResult
                    config={grossResultTableConfig(formatNumber(vat, '%'))}
                    data={[
                      {
                        totalPriceNetto,
                        vat: vatPrice,
                        totalPriceBrutto,
                      },
                    ]}
                  />
                </Accordion>
              </div>

              <div className="mb-65 px-48">
                {/* <TextArea
              label="Anmerkungen"
              value={defaultTo(this.getValue('annotation'), '')}
              onChange={value => this.setState({ annotation: value })}
            /> */}
              </div>
            </>
          )}
        </>
      )
    }
  }

  render() {
    const { calculationId } = this.props.match.params
    const projectId = getProjectId(this.props)
    const { t, history } = this.props
    const { errors, success } = this.state

    return (
      <PageLayout pageTitle={t('calculation')}>
        <Head
          showManufacturerSelect={process.env.REACT_APP_ENV === 'abk'}
          subheadline={t('backToTheOverview')}
          linkTo={!isNil(projectId) ? `/projects/${projectId}/calculations` : `/calculations`}
          error={errors ? true : false}
          message={
            errors
              ? 'Konnte nicht gespeichert werden!'
              : success
              ? 'Änderungen gespeichert!'
              : undefined
          }
          actions={
            <>
              <Button
                secondary
                onClick={() =>
                  projectId
                    ? history.push(`/projects/${projectId}/new-calculation`)
                    : history.push('/calculations/new-calculation')
                }
              >
                {t('newCalculation')}
              </Button>
            </>
          }
          headline={
            <Query
              query={!isNil(projectId) ? PROJECT_QUERY : CALCULATION_PROJECT_QUERY}
              variables={{ projectId }}
              fetchPolicy="network-only"
            >
              {({ loading, data }: any | null) => {
                const calculations = defaultTo(
                  get(
                    data,
                    `${!isNil(projectId) ? 'getProject' : 'getCalculationProject'}.positions`,
                  ),
                  [],
                )
                const calculationsOptions = this.transformCalculationsToOptions(calculations)

                return (
                  <Select
                    onChange={this.handleCalculationChange}
                    plain
                    options={calculationsOptions}
                    value={defaultTo(
                      calculationsOptions.find((option: IOption) => option.value === calculationId),
                      {
                        label: t('changeCalculation'),
                        value: '',
                      },
                    )}
                    maxWidth={250}
                    isLoading={loading}
                  />
                )
              }}
            </Query>
          }
        />

        {this.renderContent()}
      </PageLayout>
    )
  }
}

export default CalculationOverview
